import { isSet } from 'rise-ui/utils/nullish.js';
import regexEscape from 'rise-ui/utils/regex-escape.js';

export const matchSpaces = /\s+/;

export function singleSpaced(text) {
  return text ? text.trim().replace(matchSpaces, ' ') : undefined;
}

export const lower = str => typeof str === 'string' ? str.toLowerCase() : str;

export const string = str => isSet(str) ? '' + str : '';

/**
 * @param {string|number|(string|number)[]} delimiters
 * @returns {{delimiter:string, delimiters:string}}
 */
function parseDelimiters(delimiters) {
  delimiters = [].concat(delimiters === 0 ? '0' : delimiters || []);
  let delimiter = delimiters[delimiters.length - 1];
  delimiter = delimiter === 0 ? '0' : delimiter || '';
  delimiters = `[${delimiters.map(regexEscape).join('|')}]`;
  return { delimiters, delimiter };
}

/**
 * @param {string|number|(string|number)[]} strings
 * @param {string|number|(string|number)[]} [del]
 * @returns {string[]}
 */
export function split(strings, del = ',') {
  let { delimiters, delimiter } = parseDelimiters(del);
  strings = [].concat(strings)
    .filter(string => typeof string === 'string' || typeof string === 'number')
    .join(delimiter);
  return strings.split(new RegExp(delimiters));
}

/**
 * @param {string|number|(string|number)[]} strings
 * @param {string|number|(string|number)[]} [del]
 * @returns {string}
 */
export function join(strings, del = ',') {
  const { delimiters, delimiter } = parseDelimiters(del);
  strings = [].concat(strings || []);
  const iLast = strings.length - 1;
  return strings
    .map((string, i) => {
      const delims = [];
      if (i) {
        delims.push(`^${delimiters}`);
      }
      if (i !== iLast) {
        delims.push(`${delimiters}$`);
      }
      return (isSet(string) ? '' + string : '')
        .replace(new RegExp(delims.join('|'), 'g'), '');
    })
    .join(delimiter);
}

export function replaceStart(haystack, needle, replacement) {
  let result = haystack ? ('' + haystack) : '';
  needle = needle ? ('' + needle) : '';
  if (result.startsWith(needle)) {
    result = replacement + result.substr(needle.length);
  }
  return result;
}

const STRING_TITLEIZE_REGEXP = /\b([a-z\u00c0-\u024f])/g;

export function titleize(value) {
  value = (value || '') + '';
  return value.replace(STRING_TITLEIZE_REGEXP, match => match.toUpperCase());
}

const matchWrd = /[a-z]/i;
const matchNum = /[0-9]/;

const wrd = Symbol();
const num = Symbol();
const del = Symbol();

const types = [
  [ wrd, matchWrd],
  [ num, matchNum ],
  [ del ],
];

export function commonWordsDelimiter(strings) {
  return strings.map(string => {
    const parts = [];
    if (!string) {
      return parts;
    }
    let word = '';
    let lastType;
    for (let i = 0, imax = string.length; i < imax; ++i) {
      const char = string[i];
      for (let w = 0, wmax = types.length; w < wmax; ++w) {
        const [ type, match ] = types[w];
        if (type === del || match.test(char)) {
          if (lastType !== type) {
            if (word) {
              parts.push(word);
            }
            if (lastType !== del && type !== del) {
              parts.push('');
            }
            word = '';
          }
          lastType = type;
          word += char;
          break;
        }
      }
    }
    if (word) {
      parts.push(word);
    }
    if (lastType && lastType !== del) {
      parts.push('');
    }
    return parts;
  });
}

export function firstGroupReducer(rootGroup, options = {}) {
  const result = [];
  if (!rootGroup.next.size) {
    return result;
  }
  const { minOccurrence = 0 } = options;
  const addResult = (parts, source) => {
    if (source.size >= minOccurrence) {
      result.push({ parts, source: Array.from(source) });
    }
  };
  rootGroup.next.forEach((g0, delimiter) => {
    if (!g0.next.size) {
      const { source } = g0;
      if (source.size > 1) {
        addResult([ delimiter ], source);
      }
      return;
    }
    g0.next.forEach((g1, word) => {
      const { source } = g1;
      let groupName = delimiter + word;
      const parts = [ delimiter, word ];
      let sourceCount = source.size;
      let group = g1;
      let string;
      let done = false;
      while (group.next.size) {
        [ string, group ] = group.next.entries().next().value;
        if (!group) {
          break;
        }
        if (done || (done = group.source.size !== sourceCount)) {
          break;
        } else {
          groupName += string;
          parts.push(string);
        }
      }
      if (source.size > 1) {
        addResult(parts, source);
      }
    });
  });
  return result;
}

export function findCommonWords(strings, options = {}) {
  const {
    startsWith = true, endsWith,
    delimiter = commonWordsDelimiter,
    reduceGroups = firstGroupReducer,
  } = options;
  // Split out words, numbers, and delimiters.
  strings = delimiter(strings);

  const rootStringsSet = new Set();
  // MapTree of groups of common strings.
  const rootGroup = { start: 0, end: 0, source: rootStringsSet, next: new Map() };

  if (startsWith || true) {
    // Tracks which group each string is currently working in during iteration
    const partGroups = [];
    // Copy these so we can remove strings we are done with as we go.
    const stringsRemaining = strings.map((string, i) => {
      // Add all strings to the root group
      rootStringsSet.add(i);
      // Set every string part-group to the root group
      partGroups.push(rootGroup);
      // Just return the string; we're dual-purposing the copy to iterate above.
      return string;
    });

    // For looping over parts.
    let partIndex = 0;
    // Loop parts as long as we have strings left.
    while (stringsRemaining.length) {
      // Loop through every remaining string.
      for (let index = 0; index < stringsRemaining.length; ++index) {
        let string = stringsRemaining[index];
        // If this is first time through then setup meta-data.
        if (!partIndex) {
          string = stringsRemaining[index] = { index, parts: string };
        }
        const { index: stringIndex, parts } = string;
        const part = parts[partIndex];
        const partGroup = partGroups[stringIndex];
        // If remaining parts are empty or uncommon then dump rest of string
        // into last part-group and remove string from iteration.
        const isNoneRemaining = partIndex >= parts.length;
        const isRemainingUncommon = partGroup.source.length === 1;
        if (isNoneRemaining || isRemainingUncommon) {
          if (isRemainingUncommon) {
            partGroup.name = [ ...parts ];
          }
          stringsRemaining.splice(index--, 1);
          continue;
        }
        // Get/create group for this string's part
        let group = partGroup.next.get(part);
        if (!group) {
          partGroup.next.set(part, group = {
            start: 0,
            end: partIndex + 1,
            source: new Set(),
            next: new Map(),
          });
        }
        // Add this string as a source for this commonality group
        group.source.add(stringIndex);
        // Setup string part-group to point at next part-group.
        partGroups[stringIndex] = group;
      }
      ++partIndex;
    }
  }

  // TODO search for strings at end/both/middle

  return reduceGroups(rootGroup, options);
}

export function strOrNumEq(a, b) {
  // Return true if they are strictly equal
  if (a === b) {
    return true;
  }
  const aSet = Boolean(a);
  const bSet = Boolean(b);
  // Return false if one is truthy and the other falsy
  if (aSet !== bSet) {
    return false;
  }
  // If both are falsy return false if only one NaN, else true
  if (!aSet) {
    const aNaN = Number.isNaN(a);
    const bNaN = Number.isNaN(b);
    return aNaN || bNaN ? aNaN && bNaN : true;
  }
  const aNumber = typeof a === 'number';
  const aString = typeof a === 'string';
  const bNumber = typeof b === 'number';
  const bString = typeof b === 'string';
  // Return false if types are not string or number
  if (!(aNumber || aString) || !(bNumber || bString)) {
    return false;
  }
  // If one is string return if they are equal as strings
  if (aString || bString) {
    return aString && bString ? false
      : (aString ? a : String(a)) === (bString ? b : String(b));
  }
  // Return false, as they must be unequal numbers
  return false;
}

export function getFirstParagraph(text) {
  return (((text || '') + '')
      .split('\n')
      .find(t => t) ?? ''
  ).trim();
}
